import { Component, inject, OnInit, ChangeDetectorRef } from '@angular/core';
import * as XLSX from 'xlsx';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatTabsModule } from '@angular/material/tabs';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatRippleModule } from '@angular/material/core';
import { FormsModule } from '@angular/forms';
import { ExpenseService, GroupedExpense } from '../../core/services/expense.service';
import { ActivatedRoute } from '@angular/router';
import { TripService } from '../../core/services/trip.service';
import { UserService } from '../../core/services/user.service';
import { FamilyService, Family } from '../../core/services/family.service';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { CopyConfirmationDialogComponent } from './copy-confirmation-dialog.component';
import { ToastComponent } from '../../shared/components/toast/toast.component';
import { DraggableTabsDirective } from '../../shared/directives/draggable-tabs.directive';
import { firstValueFrom } from 'rxjs';


@Component({
    selector: 'app-reports',
    standalone: true,
    imports: [
        CommonModule,
        MatCardModule,
        MatButtonModule,
        MatIconModule,
        MatButtonToggleModule,
        MatTabsModule,
        MatMenuModule,
        MatSelectModule,
        MatFormFieldModule,
        MatDialogModule,
        FormsModule,
        MatProgressBarModule,
        DraggableTabsDirective,
        MatRippleModule
    ],
    templateUrl: './reports.component.html',
    styleUrls: ['./reports.component.css']
})
export class ReportsComponent implements OnInit {
    expenseService = inject(ExpenseService);
    tripService = inject(TripService);
    userService = inject(UserService);
    familyService = inject(FamilyService); // Inject FamilyService
    route = inject(ActivatedRoute);
    cd = inject(ChangeDetectorRef);
    dialog = inject(MatDialog);
    snackBar = inject(MatSnackBar);

    // View State
    reportMonth: string = '';
    reportYear: number = new Date().getFullYear();
    reportScope: 'month' | 'year' = 'month';
    reportTabIndex = 0;
    expandedCategory: string | null = null;

    // Data State
    groupedExpenses: GroupedExpense[] = [];

    // Trip Settlements
    isTripContext = false;
    memberBalances: { uid: string, name: string, balance: number, photoURL?: string, paid: number, share: number }[] = [];
    settlements: { from: string, to: string, amount: number, fromName: string, toName: string }[] = [];
    tripMembers: any[] = []; // Cache for member profiles (Renamed to members in mind, but keeping var name for minimal diff)

    // UI State
    showTripSelector = false;
    selectedTripId: string | null = null;
    trips$ = this.tripService.trips$;

    constructor() {
        const today = new Date();
        this.reportMonth = today.toISOString().slice(0, 7); // YYYY-MM
        this.reportYear = today.getFullYear();
    }

    ngOnInit() {
        // 1. Subscribe to Data Stream ONCE
        this.expenseService.groupedExpenses$.subscribe(groups => {
            this.groupedExpenses = groups;
            if (this.isTripContext) {
                this.calculateSettlements();
            }
            this.cd.detectChanges();
        });

        // 2. Handle Route Changes
        this.route.paramMap.subscribe(params => {
            const type = params.get('type');

            if (type === 'trips') {
                this.showTripSelector = true;
                this.isTripContext = true;
                this.selectedTripId = null; // Reset selection
                this.expenseService.setContext('trip', 'none_selected');
                this.settlements = [];
                this.memberBalances = [];
                this.tripMembers = []; // Clear previous members

            } else {
                this.showTripSelector = false;
                this.isTripContext = false;
                this.selectedTripId = null;
                this.expenseService.setContext('family', 'default');
                // Load Family Members for Name Resolution
                this.loadFamilyMembers();
            }
            this.cd.detectChanges();
        });
    }

    async loadFamilyMembers() {
        const family = await firstValueFrom(this.familyService.currentFamily$) as Family | null;
        if (family && family.members) {
            const promises = family.members.map((uid: string) => this.userService.getProfile(uid));
            const profiles = await Promise.all(promises);
            this.tripMembers = profiles.filter((p: any) => !!p).map((p: any) => p!);
            this.cd.detectChanges();
        }
    }

    selectedTripName: string = '';

    async onTripChange(tripId: string) {
        if (tripId) {
            this.selectedTripId = tripId;
            this.expenseService.setContext('trip', tripId);

            const trip = this.tripService.getTripSnapshot(tripId);
            this.selectedTripName = trip ? trip.name : 'Trip';

            // Load members to help with name resolution and settlement calculation
            await this.loadTripMembers(tripId);
            this.calculateSettlements();
        }
    }

    async loadTripMembers(tripId: string) {
        const trip = this.tripService.getTripSnapshot(tripId);
        if (!trip || !trip.members) return;

        const promises = trip.members.map(uid => this.userService.getProfile(uid));
        const profiles = await Promise.all(promises);
        this.tripMembers = profiles.filter(p => !!p).map(p => p!);
    }

    toggleCategory(name: string) {
        if (this.expandedCategory === name) {
            this.expandedCategory = null;
        } else {
            this.expandedCategory = name;
        }
    }

    changeReportMonth(delta: number) {
        const [year, month] = this.reportMonth.split('-').map(Number);
        const date = new Date(year, month - 1 + delta, 1);
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        this.reportMonth = `${y}-${m}`;
        this.reportYear = y;
        this.calculateSettlements(); // Recalculate if date changes (though settlement usually is whole trip?)
        // Actually, groupedExpenses update will trigger it.
        // Wait, Settlement for a TRIP is usually for the WHOLE trip, independent of month filter?
        // Ideally, yes. But usually 'Reports' are filtered.
        // For 'Settlement' tab, maybe we should ignore date filter? 
        // For now, let's respect the filter, or maybe Trip Reports shouldn't have date filters?
        // "Trip Reports" usually encompass the whole trip duration.
        // I'll leave it filtered by `groupedExpenses` for now, assuming the user selects "Year" or adequate range.
        // OR better: When in Trip mode, default to showing ALL expenses?
        // That's a bigger change. I'll stick to filtering.
    }

    changeReportYear(delta: number) {
        this.reportYear += delta;
    }

    get currentReportStats() {
        const isYear = this.reportScope === 'year';
        const filterPrefix = isYear ? String(this.reportYear) : this.reportMonth;

        let total = 0;
        const catTotals: { [key: string]: number } = {};
        const catItems: { [key: string]: any[] } = {};
        const timeTotals: { [key: string]: number } = {};

        // Aggregate data
        this.groupedExpenses.forEach(group => {
            // TRIP CONTEXT: Ignore Date Filter, show all.
            // FAMILY CONTEXT: Apply Date Filter.
            if (this.isTripContext || group.date.startsWith(filterPrefix)) {
                // For Time Breakdown
                const timeKey = isYear ? group.date.slice(0, 7) : group.date;
                timeTotals[timeKey] = (timeTotals[timeKey] || 0) + group.total;

                // For Categories
                group.items.forEach(item => {
                    total += item.amount;
                    catTotals[item.category] = (catTotals[item.category] || 0) + item.amount;

                    if (!catItems[item.category]) catItems[item.category] = [];
                    catItems[item.category].push({ ...item, date: group.date });
                });
            }
        });

        // Process Categories
        const categories = Object.entries(catTotals)
            .map(([name, amount]) => {
                const items = catItems[name] || [];
                const details = items
                    .map(item => ({
                        date: item.date,
                        amount: item.amount,
                        description: item.description || ''
                    }))
                    .sort((a, b) => b.date.localeCompare(a.date));

                return {
                    name,
                    amount,
                    percentage: total > 0 ? (amount / total) * 100 : 0,
                    details
                };
            })
            .sort((a, b) => b.amount - a.amount);

        // Process Timeline
        const timeline = Object.entries(timeTotals)
            .map(([key, amount]) => ({
                label: key,
                amount,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));

        const maxAmt = Math.max(...timeline.map(t => t.amount), 1);

        const timelineWithPerc = timeline.map(t => ({
            ...t,
            percentage: (t.amount / maxAmt) * 100
        }));

        return { total, categories, timeline: timelineWithPerc };
    }

    calculateSettlements() {
        if (!this.isTripContext || !this.tripMembers.length) return;

        const balances: { [uid: string]: number } = {};
        const paid: { [uid: string]: number } = {};
        const share: { [uid: string]: number } = {};

        // Initialize 0
        this.tripMembers.forEach(m => {
            balances[m.uid] = 0;
            paid[m.uid] = 0;
            share[m.uid] = 0;
        });

        this.groupedExpenses.forEach(group => {
            const isYear = this.reportScope === 'year';
            const filterPrefix = isYear ? String(this.reportYear) : this.reportMonth;

            if (!group.date.startsWith(filterPrefix)) return;

            group.items.forEach(item => {
                const payerId = item.user;
                const amount = item.amount;

                // Add to payer
                balances[payerId] = (balances[payerId] || 0) + amount;
                paid[payerId] = (paid[payerId] || 0) + amount;

                // Deduct from consumers
                let consumers = item.sharedBy;

                // Logic: If sharedBy is undefined or empty, it implies "Everyone involved in the trip".
                if (!consumers || consumers.length === 0) {
                    consumers = this.tripMembers.map(m => m.uid);
                }

                if (item.splitType === 'percentage' && item.splitDetails) {
                    // Percentage Split Strategy
                    Object.entries(item.splitDetails as { [key: string]: number }).forEach(([uid, percentage]) => {
                        const splitAmt = (amount * percentage) / 100;
                        balances[uid] = (balances[uid] || 0) - splitAmt;
                        share[uid] = (share[uid] || 0) + splitAmt;
                    });
                } else {
                    // Equal Split Strategy
                    if (consumers.length > 0) {
                        const splitAmount = amount / consumers.length;
                        consumers.forEach((uid: string) => {
                            balances[uid] = (balances[uid] || 0) - splitAmount;
                            share[uid] = (share[uid] || 0) + splitAmount;
                        });
                    }
                }
            });
        });

        // Convert balances to array
        const newMemberBalances = Object.entries(balances).map(([key, balance]) => {
            const member = this.findMember(key);
            return {
                uid: member ? member.uid : key,
                name: member ? (member.displayName || 'User') : (key === 'Unknown' ? 'Unknown User' : key),
                photoURL: member?.photoURL,
                balance,
                paid: paid[key] || 0,
                share: share[key] || 0
            };
        }).sort((a, b) => b.balance - a.balance); // Sort by Net Balance

        const newSettlements = this.resolveDebts(balances);

        // Update in next tick to avoid ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
            this.memberBalances = newMemberBalances;
            this.settlements = newSettlements;
            this.cd.detectChanges();
        });
    }

    resolveDebts(balances: { [uid: string]: number }) {
        let debtors: { uid: string, amount: number }[] = [];
        let creditors: { uid: string, amount: number }[] = [];

        Object.entries(balances).forEach(([uid, amount]) => {
            if (amount < -0.01) debtors.push({ uid, amount }); // Negative means you owe
            else if (amount > 0.01) creditors.push({ uid, amount }); // Positive means you are owed
        });

        // Sort by magnitude
        debtors.sort((a, b) => a.amount - b.amount); // Most negative first (owes most)
        creditors.sort((a, b) => b.amount - a.amount); // Most positive first (owed most)

        const results: { from: string, to: string, amount: number, fromName: string, toName: string }[] = [];

        let i = 0; // creditor index
        let j = 0; // debtor index

        while (i < creditors.length && j < debtors.length) {
            const creditor = creditors[i];
            const debtor = debtors[j];

            const credit = creditor.amount;
            const debt = Math.abs(debtor.amount); // Positive magnitude of debt

            const amount = Math.min(credit, debt);

            // Record
            const fromMem = this.findMember(debtor.uid);
            const toMem = this.findMember(creditor.uid);

            results.push({
                from: debtor.uid,
                to: creditor.uid,
                amount: amount,
                fromName: fromMem?.displayName || (debtor.uid === 'Unknown' ? 'Unknown User' : debtor.uid),
                toName: toMem?.displayName || (creditor.uid === 'Unknown' ? 'Unknown User' : creditor.uid)
            });

            // Adjust remaining
            creditor.amount -= amount;
            debtor.amount += amount; // making it less negative, towards zero

            if (creditor.amount < 0.01) i++;
            if (Math.abs(debtor.amount) < 0.01) j++;
        }

        return results;
    }

    findMember(key: string) {
        if (!key) return undefined;
        return this.tripMembers.find(m => m.uid === key || m.displayName === key || m.name === key);
    }

    downloadReport() {
        // ... existing downloadReport logic ...
        // 1. Filter Data based on current scope
        const isYear = this.reportScope === 'year';
        const filterPrefix = isYear ? String(this.reportYear) : this.reportMonth;

        const reportData: any[] = [];

        this.groupedExpenses.forEach(group => {
            if (this.isTripContext || group.date.startsWith(filterPrefix)) {
                group.items.forEach(item => {

                    // Format sharedBy for CSV
                    let sharedWith = 'Everyone';

                    if (item.splitType === 'percentage' && item.splitDetails) {
                        sharedWith = Object.entries(item.splitDetails).map(([uid, pct]) => {
                            const name = this.resolveName(uid);
                            return `${name} (${pct}%)`;
                        }).join(', ');
                    } else if (item.sharedBy && item.sharedBy.length > 0) {
                        sharedWith = item.sharedBy.map((uid: string) => {
                            return this.resolveName(uid);
                        }).join(', ');
                    }

                    reportData.push({
                        Date: item.date || group.date, // Use specific date if available
                        Category: item.category,
                        Description: item.description || '',
                        Amount: item.amount,
                        PaidBy: this.resolveName(item.user),
                        SharedWith: sharedWith
                    });
                });
            }
        });

        // 2. Sort by Date
        reportData.sort((a, b) => new Date(b.Date).getTime() - new Date(a.Date).getTime());

        // 3. Generate Worksheet
        const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(reportData);

        // 4. Generate Workbook
        const wb: XLSX.WorkBook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Expenses');

        // 5. Save File
        const fileName = `Expenses_${filterPrefix}_${new Date().getTime()}.xlsx`;
        XLSX.writeFile(wb, fileName);
    }

    downloadSettlementReport() {
        if (!this.settlements || this.settlements.length === 0) return;

        const data = this.settlements.map(s => ({
            From: s.fromName,
            To: s.toName,
            Amount: s.amount
        }));

        const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);
        const wb: XLSX.WorkBook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Settlements');

        const fileName = `Settlements_${this.selectedTripName || 'Trip'}_${new Date().getTime()}.xlsx`;
        XLSX.writeFile(wb, fileName);
    }

    downloadBreakdownReport() {
        if (!this.memberBalances || this.memberBalances.length === 0) return;

        const data = this.memberBalances.map(m => ({
            'Member Name': m.name,
            'Total Paid': m.paid,
            'Total Share': m.share,
            'Net Balance (+Get, - Owe)': m.balance
        }));

        const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);
        const wb: XLSX.WorkBook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Breakdown');

        const fileName = `Breakdown_${this.selectedTripName || 'Trip'}_${new Date().getTime()}.xlsx`;
        XLSX.writeFile(wb, fileName);
    }

    async copyTripToFamily() {
        try {
            // 1. Check if user has a family
            const currentUser = this.userService.currentProfileSnapshot;
            if (!currentUser || !currentUser.familyId) {
                this.showToast('Error', 'Please create a family first before copying trip expenses.', 'error');
                return;
            }

            // 2. Ensure "Tour" category exists in family categories
            const currentUserId = currentUser.uid;
            await this.ensureTourCategory(currentUserId);

            // 3. Get current user's share from memberBalances
            const myBalance = this.memberBalances.find(m => m.uid === currentUserId);
            if (!myBalance) {
                this.showToast('Error', 'Could not find your expenses in this trip.', 'error');
                return;
            }

            // 4. Calculate preview data
            const tripExpenses = this.groupedExpenses.flatMap(g => g.items);
            let eligibleExpenses = [];
            let totalAmount = 0;

            for (const expense of tripExpenses) {
                // Calculate ONLY my share of this expense (regardless of who paid)
                // This ensures we capture Consumption (e.g. 400) rather than just Spending (e.g. 100)
                const userShare = this.calculateUserShare(expense, currentUserId);
                if (userShare === 0) continue;

                const familyDescription = `${this.selectedTripName}_${expense.description}`;
                const isDuplicate = await this.checkDuplicateExpense(
                    currentUser.familyId,
                    familyDescription,
                    expense.date,
                    currentUserId
                );

                if (!isDuplicate) {
                    // Round to 2 decimal places
                    const roundedShare = Math.round(userShare * 100) / 100;
                    eligibleExpenses.push({ expense, userShare: roundedShare, familyDescription });
                    totalAmount += roundedShare;
                }
            }

            if (eligibleExpenses.length === 0) {
                this.showToast('Info', 'No new expenses to copy. All expenses have already been copied to family.', 'error');
                return;
            }

            // 5. Show confirmation dialog
            const dialogRef = this.dialog.open(CopyConfirmationDialogComponent, {
                data: {
                    tripName: this.selectedTripName,
                    expenseCount: eligibleExpenses.length,
                    totalAmount: totalAmount,
                    category: 'Tour'
                }
            });

            const confirmed = await dialogRef.afterClosed().toPromise();
            if (!confirmed) {
                return;
            }

            // 6. Perform the copy
            let copiedCount = 0;
            for (const item of eligibleExpenses) {
                await this.expenseService.addExpense({
                    amount: item.userShare,
                    description: item.familyDescription,
                    date: item.expense.date,
                    category: 'Tour',
                    user: currentUserId,
                    contextType: 'family',
                    contextId: currentUser.familyId
                });
                copiedCount++;
            }

            this.showToast('Success', `Successfully copied ${copiedCount} expense(s) to family!`, 'success');
        } catch (error) {
            console.error('Error copying to family:', error);
            this.showToast('Error', 'Failed to copy expenses. Please try again.', 'error');
        }
    }

    private calculateUserShare(expense: any, userId: string): number {
        const amount = expense.amount;
        let consumers = expense.sharedBy;

        if (!consumers || consumers.length === 0) {
            consumers = this.tripMembers.map(m => m.uid);
        }

        // Check if user is part of this expense
        if (!consumers.includes(userId)) return 0;

        if (expense.splitType === 'percentage' && expense.splitDetails) {
            const percentage = expense.splitDetails[userId] || 0;
            return (amount * percentage) / 100;
        } else {
            return amount / consumers.length;
        }
    }

    private async ensureTourCategory(userId: string) {
        const categories = await this.expenseService.getCategoriesForType('family');
        const tourExists = categories.some((c: { name: string }) => c.name.toLowerCase() === 'tour');

        if (!tourExists) {
            await this.expenseService.addCategory('Tour', 'family');
        }
    }

    private async checkDuplicateExpense(familyId: string, description: string, date: string, userId: string): Promise<boolean> {
        try {
            // Query Firestore directly for family expenses with matching description, date AND user
            const { query, collection, where, getDocs } = await import('@angular/fire/firestore');
            const expensesRef = collection(this.expenseService['firestore'], 'expenses');
            const q = query(
                expensesRef,
                where('contextType', '==', 'family'),
                where('contextId', '==', familyId),
                where('description', '==', description),
                where('date', '==', date),
                where('user', '==', userId)
            );

            const snapshot = await getDocs(q);
            return !snapshot.empty;
        } catch (error) {
            console.warn('Could not check for duplicates, allowing copy:', error);
            // If we can't check, allow the copy (better to have duplicates than block legitimate copies)
            return false;
        }
    }

    private showToast(title: string, message: string, type: 'success' | 'error') {
        this.snackBar.openFromComponent(ToastComponent, {
            data: { title, message, type },
            duration: 3000,
            panelClass: 'custom-toast-container',
            horizontalPosition: 'end',
            verticalPosition: 'bottom'
        });
    }

    resolveName(uid: string): string {
        const m = this.findMember(uid);
        if (m && m.displayName) {
            return m.displayName.split(' ')[0];
        }
        return uid || 'User';
    }
}
